/**
 * Native Google + Apple SSO via @capgo/capacitor-social-login.
 *
 * The plugin shells out to:
 *   iOS Google   → Sign in with Google iOS SDK
 *   Android Google → CredentialManager / Google Identity Services
 *   iOS Apple    → AuthenticationServices.framework (ASAuthorizationAppleIDProvider)
 *   Android Apple → Apple's web Sign in with Apple inside a Custom Tab
 *
 * Config comes from build-time env vars so the user can drop real IDs in
 * once the Google Cloud Console + Apple Developer portal setup is done:
 *
 *   VITE_GOOGLE_IOS_CLIENT_ID       — iOS OAuth client ID from Google Cloud
 *                                     (looks like 12345-abc.apps.googleusercontent.com)
 *   VITE_GOOGLE_ANDROID_CLIENT_ID   — Android OAuth client ID (separate from iOS)
 *   VITE_GOOGLE_WEB_CLIENT_ID       — Web client ID; Android requires this
 *                                     to issue ID tokens (idTokenClientId field)
 *   VITE_APPLE_SERVICE_ID           — Apple Services ID for the WEB flow only
 *                                     (reused from the web Apple button)
 *   VITE_APPLE_REDIRECT_URI         — Apple form-post URL (web only)
 *
 * Native iOS Apple uses the App ID (bundle ID com.packetprospector.field)
 * automatically — no config needed beyond enabling the capability in Xcode.
 *
 * When any of these env vars are missing the plugin still initializes; the
 * relevant button stays hidden via isGoogleSsoConfigured / isAppleSsoConfigured.
 */

import { SocialLogin } from '@capgo/capacitor-social-login';
import { Capacitor } from '@capacitor/core';

let initialized = false;
let initPromise: Promise<void> | null = null;

export function getGoogleIosClientId(): string | undefined {
  return import.meta.env.VITE_GOOGLE_IOS_CLIENT_ID;
}

export function getGoogleAndroidClientId(): string | undefined {
  return import.meta.env.VITE_GOOGLE_ANDROID_CLIENT_ID;
}

export function getGoogleWebClientId(): string | undefined {
  return import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID;
}

export function isGoogleSsoConfigured(): boolean {
  if (Capacitor.getPlatform() === 'ios') return Boolean(getGoogleIosClientId());
  if (Capacitor.getPlatform() === 'android') return Boolean(getGoogleAndroidClientId());
  return false;
}

/**
 * Native Sign in with Apple availability. iOS needs no client config — the
 * plugin uses AuthenticationServices with the App ID; the capability is the
 * com.apple.developer.applesignin entitlement plus the Apple Developer
 * portal toggle. Android would go through Apple's web flow and needs the
 * Service ID setup we haven't wired, so keep it iOS-only.
 */
export function isNativeAppleConfigured(): boolean {
  return Capacitor.getPlatform() === 'ios';
}

/**
 * Initialize the social-login plugin. Call once at app startup from native
 * shells only (no-op on web — web uses different code paths). Subsequent
 * calls return the cached promise so it's safe to await from multiple
 * places.
 */
export async function initSocialLogin(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const google = isGoogleSsoConfigured()
      ? {
          // Same client config initializes both platforms; the plugin picks
          // the right one per its iOS / Android implementation.
          iOSClientId: getGoogleIosClientId(),
          iOSServerClientId: getGoogleWebClientId(),
          webClientId: getGoogleWebClientId(),
          mode: 'online' as const,
        }
      : undefined;

    // Apple on native iOS uses the App ID — no client config needed here;
    // the capability is enabled in Xcode. Including an empty `apple: {}`
    // tells the plugin to also wire up the Apple provider so we can call
    // SocialLogin.login({ provider: 'apple' }).
    const apple = Capacitor.getPlatform() === 'ios' ? {} : undefined;

    try {
      await SocialLogin.initialize({
        ...(google ? { google } : {}),
        ...(apple ? { apple } : {}),
      });
      initialized = true;
    } catch (err) {
      console.warn('SocialLogin initialize failed:', err);
    }
  })();

  return initPromise;
}

/**
 * Trigger a native Google sign-in. Returns the ID token (a JWT) that the
 * caller should POST to /api/auth/google-sso. Throws if cancelled or if the
 * plugin is uninitialized.
 */
export async function signInWithGoogleNative(): Promise<string> {
  if (!initialized) await initSocialLogin();

  const result = await SocialLogin.login({
    provider: 'google',
    options: {
      // Request idToken explicitly so we get the JWT, not just an access token.
      // Some plugin versions need both flags depending on platform.
      forceRefreshToken: true,
    },
  });

  // Shape varies slightly per platform; ID token lives at one of these paths.
  type GoogleLoginResult = {
    result?: {
      idToken?: string;
      authentication?: { idToken?: string };
    };
  };
  const r = result as GoogleLoginResult;
  const idToken = r.result?.idToken ?? r.result?.authentication?.idToken;
  if (!idToken) {
    throw new Error('Google sign-in returned no ID token');
  }
  return idToken;
}

/**
 * Trigger native Sign in with Apple. iOS uses the system AuthenticationServices
 * sheet; on first sign-in for an Apple ID, the user may share their first/last
 * name and we get them back. On subsequent sign-ins, only the email +
 * identity token come through.
 */
export async function signInWithAppleNative(): Promise<{
  identityToken: string;
  firstName?: string;
  lastName?: string;
  nonce?: string;
}> {
  if (!initialized) await initSocialLogin();

  // Apple's iOS framework (ASAuthorizationAppleIDRequest.nonce) embeds
  // whatever value we hand it VERBATIM in the identity token's `nonce`
  // claim — there is no automatic hashing. Apple's recommended pattern,
  // and what our backend (AppleSsoTokenVerifier) checks, is:
  //   - client generates raw nonce
  //   - client sets request.nonce = SHA256(raw)
  //   - client POSTs raw nonce to backend
  //   - backend computes SHA256(raw) and compares to the token's claim
  //
  // The @capgo/capacitor-social-login plugin sets request.nonce to the
  // string we pass verbatim (see ios/Sources/SocialLoginPlugin/
  // AppleProvider.swift:210). So we hash here before handing to the
  // plugin, and return the RAW nonce so the caller can POST it. Without
  // this transform every Apple sign-in 401s with "Failed to verify
  // Apple sign-in" because the token's nonce (raw) doesn't equal the
  // backend's expected SHA256(raw).
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const rawNonce = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  const hashedNonce = await sha256Hex(rawNonce);

  const result = await SocialLogin.login({
    provider: 'apple',
    options: { nonce: hashedNonce },
  });

  type AppleLoginResult = {
    result?: {
      idToken?: string;
      identityToken?: string;
      givenName?: string;
      familyName?: string;
      profile?: { firstName?: string; lastName?: string };
    };
  };
  const r = result as AppleLoginResult;
  const identityToken = r.result?.idToken ?? r.result?.identityToken;
  if (!identityToken) {
    throw new Error('Apple sign-in returned no identity token');
  }

  return {
    identityToken,
    firstName: r.result?.givenName ?? r.result?.profile?.firstName,
    lastName: r.result?.familyName ?? r.result?.profile?.lastName,
    // Return the RAW nonce — backend hashes it and compares to the
    // token's `nonce` claim (which is the hashed value we sent to the
    // plugin above).
    nonce: rawNonce,
  };
}

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}
